import React, { useEffect, useRef } from "react";
import Lottie from "lottie-react";
import { motion, AnimatePresence } from "framer-motion";
import confettiAnimation from "../static/animations/confetti-celebration.json";

const DISPLAY_DURATION = 6000;

/**
 * RewardCelebration overlay.
 *
 * Props:
 *  - show        : boolean – mount/unmount the overlay
 *  - onComplete  : () => void – called when the overlay should be hidden
 *  - variant     : "earned" | "capped" – which visual to show
 *  - message     : optional override string
 */
const RewardCelebration = ({ show, onComplete, message, variant = "earned" }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    if (show) {
      // Clear any previous timer
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onComplete?.();
      }, DISPLAY_DURATION);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show]); // intentionally only depend on `show`

  const isCapped = variant === "capped";

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="reward-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Full-screen confetti (only for earned variant) */}
          {!isCapped && (
            <Lottie
              animationData={confettiAnimation}
              loop={false}
              autoplay={true}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            />
          )}

          {/* Centered reward card */}
          <motion.div
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: "spring", damping: 15, stiffness: 300 }}
            style={{
              position: "relative",
              zIndex: 10000,
              background: "rgba(0, 18, 51, 0.9)",
              borderRadius: "16px",
              padding: "28px 44px",
              border: "1px solid rgba(255,255,255,0.15)",
              backdropFilter: "blur(12px)",
              textAlign: "center",
              boxShadow: isCapped
                ? "0 0 40px rgba(106, 31, 191, 0.2)"
                : "0 0 40px rgba(106, 31, 191, 0.3), 0 0 80px rgba(0, 200, 83, 0.15)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "8px" }}>
              {isCapped ? "\uD83D\uDC4D" : "\uD83C\uDF89"}
            </div>
            <div
              style={{
                background: isCapped
                  ? "linear-gradient(to right, #6a1fbf, #8b5cf6)"
                  : "linear-gradient(to right, #6a1fbf, #00c853)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: "22px",
                fontWeight: 700,
                marginBottom: "6px",
              }}
            >
              {isCapped ? "Daily Limit Reached" : "Rally Token Earned!"}
            </div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "14px", maxWidth: "280px" }}>
              {message ||
                (isCapped
                  ? "Maximum daily tokens earned for hosting meetings"
                  : "You earned a token for hosting a meeting with a real visitor!")}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RewardCelebration;
